import {
  formatBytes,
  getLibraryTemplate,
  LIBRARY_TEMPLATES,
  type LibraryTemplate,
  type Project,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  Copy,
  ExternalLink,
  FolderInput,
  FolderKanban,
  FolderPlus,
  LayoutTemplate,
  MoreHorizontal,
  Pencil,
  Plus,
  Scaling,
  Trash2,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { DocumentCard } from "../components/document-card";
import {
  LibraryTemplateTile,
  UseLibraryTemplateDialog,
  useTemplateBrand,
} from "../components/library-templates";
import { MultiFormatDialog } from "../components/multi-format-dialog";
import { NewDocumentDialog } from "../components/new-document-dialog";
import { Button } from "../components/ui/button";
import { EmptyState, PageHeader, Skeleton, skeletonKeys } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenu } from "../components/ui/context-menu";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import { Field, Input, Select, Textarea } from "../components/ui/input";
import { NameDialog } from "../editor/NameDialog";
import { orpc } from "../lib/api";
import { useOrgId, useOrgRole } from "../lib/org";
import { errorMessage, formatRelative } from "../lib/utils";

function ProjectDialog({
  orgId,
  project,
  open,
  onOpenChange,
}: {
  orgId: string;
  project?: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(project?.name ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const [brandKitId, setBrandKitId] = useState<string>(project?.brandKitId ?? "");
  const kits = useQuery({ ...orpc.brand.list.queryOptions({ input: { orgId } }), enabled: open });
  const options = {
    onSuccess: async (saved: Project) => {
      await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
      await queryClient.invalidateQueries({ queryKey: orpc.brand.key() });
      onOpenChange(false);
      if (!project)
        await navigate({
          to: "/o/$orgId/projects/$projectId",
          params: { orgId, projectId: saved.id },
        });
    },
    onError: (error: Error) => toast.error(errorMessage(error)),
  };
  const create = useMutation(orpc.projects.create.mutationOptions(options));
  const update = useMutation(orpc.projects.update.mutationOptions(options));

  function submit(event: FormEvent) {
    event.preventDefault();
    const kit = brandKitId || null;
    if (project)
      update.mutate({
        orgId,
        projectId: project.id,
        name,
        description: description || null,
        ...(kit !== project.brandKitId ? { brandKitId: kit } : {}),
      });
    else create.mutate({ orgId, name, description: description || undefined, brandKitId: kit });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={project ? "Projekt bearbeiten" : "Neues Projekt"}
        description="Projekte bündeln zusammengehörige Dateien, z. B. eine Kampagne."
      >
        <form onSubmit={submit} className="space-y-4">
          <Field label="Name">
            <Input
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Herbstkampagne 2026"
            />
          </Field>
          <Field label="Beschreibung (optional)">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          {kits.data?.length === 0 ? (
            <div className="space-y-1.5">
              <p className="text-sm font-medium text-zinc-700">Brand Kit</p>
              <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
                Kein Brand Kit – Dateien starten mit neutralen Farben und Schriften.{" "}
                <Link
                  to="/o/$orgId/brand"
                  params={{ orgId }}
                  className="font-medium text-brand-700 hover:underline"
                >
                  In „Marke“ anlegen
                </Link>
              </p>
            </div>
          ) : kits.data && kits.data.length > 1 ? (
            <Field
              label="Brand Kit"
              hint="Dateien im Projekt übernehmen Farben, Schriften und Logos dieses Kits."
            >
              <Select value={brandKitId} onChange={(e) => setBrandKitId(e.target.value)}>
                <option value="">
                  Standard ({kits.data.find((kit) => kit.isDefault)?.name ?? "Standard-Kit"})
                </option>
                {kits.data
                  .filter((kit) => !kit.isDefault)
                  .map((kit) => (
                    <option key={kit.id} value={kit.id}>
                      {kit.name}
                    </option>
                  ))}
              </Select>
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {project ? "Speichern" : "Projekt anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A few library templates across the formats (fresh organizations). */
const STARTER_TEMPLATES = [
  "post-neuigkeit",
  "story-save-the-date",
  "flyer-a5-event",
  "reel-titel-untertitel",
];

function StartWithTemplate({ orgId }: { orgId: string }) {
  const brand = useTemplateBrand(orgId);
  const [using, setUsing] = useState<LibraryTemplate | null>(null);
  const starters = STARTER_TEMPLATES.flatMap((id) => {
    const template = getLibraryTemplate(id);
    return template ? [template] : [];
  });
  return (
    <section className="mb-10">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-zinc-700">Mit einer Vorlage starten</h2>
          <p className="text-sm text-zinc-500">
            Fertige Layouts für Posts, Stories, Flyer und Reels – passen sich an deine Marke an.
          </p>
        </div>
        <Link
          to="/o/$orgId/templates"
          params={{ orgId }}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
        >
          <LayoutTemplate className="size-4" /> Alle {LIBRARY_TEMPLATES.length} Vorlagen
        </Link>
      </div>
      {brand ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {starters.map((template) => (
            <LibraryTemplateTile
              key={template.id}
              template={template}
              brand={brand}
              onClick={() => setUsing(template)}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {skeletonKeys(4).map((key) => (
            <Skeleton key={key} className="h-60" />
          ))}
        </div>
      )}
      {using ? (
        <UseLibraryTemplateDialog orgId={orgId} template={using} onClose={() => setUsing(null)} />
      ) : null}
    </section>
  );
}

export function ProjectsPage() {
  const orgId = useOrgId();
  const { can } = useOrgRole(orgId);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const org = useQuery(orpc.orgs.get.queryOptions({ input: { orgId } }));
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: showArchived } }),
  );
  const recent = useQuery(orpc.documents.recent.queryOptions({ input: { orgId, limit: 4 } }));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
  };
  const onError = (error: Error) => toast.error(errorMessage(error));
  const updateProject = useMutation(
    orpc.projects.update.mutationOptions({ onSuccess: invalidate, onError }),
  );
  const removeProject = useMutation(orpc.projects.remove.mutationOptions({ onError }));
  const duplicate = useMutation(
    orpc.documents.duplicate.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("Kopie angelegt");
      },
      onError,
    }),
  );
  const removeDocument = useMutation(
    orpc.documents.remove.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("In den Papierkorb verschoben");
      },
      onError,
    }),
  );
  const projectItems = (project: Project) => (
    <>
      <DropdownItem
        onSelect={() =>
          void navigate({
            to: "/o/$orgId/projects/$projectId",
            params: { orgId, projectId: project.id },
          })
        }
      >
        <FolderKanban /> Öffnen
      </DropdownItem>
      <DropdownItem
        onSelect={() => window.open(`/o/${orgId}/projects/${project.id}`, "_blank", "noopener")}
      >
        <ExternalLink /> In neuem Tab öffnen
      </DropdownItem>
      {can("project.update") ? (
        <>
          <DropdownSeparator />
          <DropdownItem onSelect={() => setEditingProject(project)}>
            <Pencil /> Bearbeiten …
          </DropdownItem>
          <DropdownItem
            onSelect={() =>
              updateProject.mutate({ orgId, projectId: project.id, archived: !project.archivedAt })
            }
          >
            {project.archivedAt ? <ArchiveRestore /> : <Archive />}
            {project.archivedAt ? "Wiederherstellen" : "Archivieren"}
          </DropdownItem>
        </>
      ) : null}
      {can("project.delete") ? (
        <>
          <DropdownSeparator />
          <DropdownItem danger onSelect={() => setDeletingProject(project)}>
            <Trash2 /> In den Papierkorb …
          </DropdownItem>
        </>
      ) : null}
    </>
  );

  return (
    <>
      <PageHeader
        title="Projekte"
        description={
          org.data
            ? `${org.data.name} · ${org.data.memberCount} Mitglied${org.data.memberCount === 1 ? "" : "er"} · ${formatBytes(org.data.storageUsedBytes)} belegt`
            : undefined
        }
        actions={
          can("project.create") ? (
            <Button onClick={() => setCreating(true)}>
              <FolderPlus /> Neues Projekt
            </Button>
          ) : null
        }
      />

      {recent.data?.length === 0 && can("document.create") ? (
        <StartWithTemplate orgId={orgId} />
      ) : null}

      {recent.data && recent.data.length > 0 ? (
        <section className="mb-10">
          <h2 className="mb-3 text-sm font-semibold text-zinc-700">Zuletzt bearbeitet</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {recent.data.map((document) => (
              <DocumentCard
                key={document.id}
                orgId={orgId}
                document={document}
                subtitle={document.projectName}
                menuItems={
                  <>
                    <DropdownItem
                      onSelect={() =>
                        void navigate({
                          to: "/o/$orgId/projects/$projectId",
                          params: { orgId, projectId: document.projectId },
                        })
                      }
                    >
                      <FolderKanban /> Zum Projekt
                    </DropdownItem>
                    {can("document.create") ? (
                      <DropdownItem
                        onSelect={() => duplicate.mutate({ orgId, documentId: document.id })}
                      >
                        <Copy /> Duplizieren
                      </DropdownItem>
                    ) : null}
                    {can("document.update") ? (
                      <>
                        <DropdownSeparator />
                        <DropdownItem
                          danger
                          onSelect={() => removeDocument.mutate({ orgId, documentId: document.id })}
                        >
                          <Trash2 /> In den Papierkorb
                        </DropdownItem>
                      </>
                    ) : null}
                  </>
                }
              />
            ))}
          </div>
        </section>
      ) : null}

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-zinc-700">Alle Projekte</h2>
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-zinc-300"
            />
            Archivierte anzeigen
          </label>
        </div>
        {projects.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {skeletonKeys(3).map((key) => (
              <Skeleton key={key} className="h-32" />
            ))}
          </div>
        ) : projects.data?.length === 0 ? (
          <EmptyState
            icon={<FolderKanban />}
            title="Noch keine Projekte"
            description="Lege ein Projekt an, um darin Social-Posts, Flyer oder Reels zu erstellen."
            action={
              can("project.create") ? (
                <Button onClick={() => setCreating(true)}>Erstes Projekt anlegen</Button>
              ) : null
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.data?.map((project) => (
              <ContextMenu key={project.id} menu={projectItems(project)}>
                <Link
                  to="/o/$orgId/projects/$projectId"
                  params={{ orgId, projectId: project.id }}
                  className="group rounded-xl border border-zinc-200 bg-white p-5 shadow-xs transition hover:border-brand-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                      <FolderKanban className="size-5" />
                    </span>
                    {project.archivedAt ? (
                      <span className="rounded bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
                        Archiviert
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-4 truncate font-medium text-zinc-900 group-hover:text-brand-700">
                    {project.name}
                  </p>
                  <p className="mt-1 line-clamp-1 text-sm text-zinc-500">
                    {project.description || "Keine Beschreibung"}
                  </p>
                  <p className="mt-3 text-xs text-zinc-400">
                    {project.documentCount} Datei{project.documentCount === 1 ? "" : "en"} ·{" "}
                    {formatBytes(project.sizeBytes)} · {formatRelative(project.updatedAt)}
                  </p>
                </Link>
              </ContextMenu>
            ))}
          </div>
        )}
      </section>
      {creating ? <ProjectDialog orgId={orgId} open={creating} onOpenChange={setCreating} /> : null}
      {editingProject ? (
        <ProjectDialog
          orgId={orgId}
          project={editingProject}
          open
          onOpenChange={(open) => !open && setEditingProject(null)}
        />
      ) : null}
      <ConfirmDialog
        open={deletingProject !== null}
        onOpenChange={(open) => !open && setDeletingProject(null)}
        danger
        title={`„${deletingProject?.name ?? "Projekt"}“ in den Papierkorb?`}
        description="Das Projekt und alle Dateien darin landen im Papierkorb und werden nach 30 Tagen endgültig gelöscht. Medien bleiben in der Mediathek."
        confirmLabel="In den Papierkorb"
        onConfirm={async () => {
          if (!deletingProject) return;
          await removeProject.mutateAsync({ orgId, projectId: deletingProject.id });
          await invalidate();
          toast.success("Projekt in den Papierkorb verschoben");
        }}
      />
    </>
  );
}

function MoveDocumentDialog({
  orgId,
  documentId,
  currentProjectId,
  onClose,
}: {
  orgId: string;
  documentId: string;
  currentProjectId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const projects = useQuery(
    orpc.projects.list.queryOptions({ input: { orgId, includeArchived: false } }),
  );
  const [target, setTarget] = useState("");
  const move = useMutation(
    orpc.documents.move.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
        toast.success("Datei verschoben");
        onClose();
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  const options = projects.data?.filter((project) => project.id !== currentProjectId) ?? [];
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title="Datei verschieben">
        <Field label="Zielprojekt">
          <Select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Projekt wählen …</option>
            {options.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
        <DialogFooter>
          <Button
            disabled={!target}
            loading={move.isPending}
            onClick={() => move.mutate({ orgId, documentId, projectId: target })}
          >
            Verschieben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectPage() {
  const orgId = useOrgId();
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useOrgRole(orgId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moving, setMoving] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  const [multiFormat, setMultiFormat] = useState<{
    id: string;
    title: string;
    formatPreset: string;
  } | null>(null);
  const project = useQuery(orpc.projects.get.queryOptions({ input: { orgId, projectId } }));
  const documents = useQuery(orpc.documents.list.queryOptions({ input: { orgId, projectId } }));
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
    await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
  };
  const onError = (error: Error) => toast.error(errorMessage(error));
  const update = useMutation(
    orpc.projects.update.mutationOptions({ onSuccess: invalidate, onError }),
  );
  const remove = useMutation(orpc.projects.remove.mutationOptions({ onError }));
  const duplicate = useMutation(
    orpc.documents.duplicate.mutationOptions({ onSuccess: invalidate, onError }),
  );
  const markTemplate = useMutation(
    orpc.documents.update.mutationOptions({
      onSuccess: async (document) => {
        await invalidate();
        await queryClient.invalidateQueries({ queryKey: orpc.templates.key() });
        toast.success(
          document.isTemplate ? "Als Vorlage markiert" : "Nicht mehr als Vorlage markiert",
        );
      },
      onError,
    }),
  );
  const removeDocument = useMutation(
    orpc.documents.remove.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        toast.success("In den Papierkorb verschoben");
      },
      onError,
    }),
  );
  const rename = useMutation(orpc.documents.update.mutationOptions({ onError }));

  if (project.isError) {
    return (
      <EmptyState
        icon={<FolderKanban />}
        title="Projekt nicht gefunden"
        description="Es wurde gelöscht oder du hast keinen Zugriff."
      />
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/o/$orgId" params={{ orgId }} className="hover:text-zinc-900">
            Projekte
          </Link>
        }
        title={project.data?.name ?? " "}
        description={project.data?.description ?? undefined}
        actions={
          <>
            {can("project.update") ? (
              <DropdownMenu>
                <DropdownTrigger asChild>
                  <Button variant="secondary" size="icon" aria-label="Projektaktionen">
                    <MoreHorizontal />
                  </Button>
                </DropdownTrigger>
                <DropdownContent>
                  <DropdownItem onSelect={() => setEditing(true)}>
                    <Pencil /> Bearbeiten
                  </DropdownItem>
                  <DropdownItem
                    onSelect={() =>
                      update.mutate({ orgId, projectId, archived: !project.data?.archivedAt })
                    }
                  >
                    {project.data?.archivedAt ? <ArchiveRestore /> : <Archive />}
                    {project.data?.archivedAt ? "Wiederherstellen" : "Archivieren"}
                  </DropdownItem>
                  {can("project.delete") ? (
                    <>
                      <DropdownSeparator />
                      <DropdownItem danger onSelect={() => setDeleting(true)}>
                        <Trash2 /> In den Papierkorb
                      </DropdownItem>
                    </>
                  ) : null}
                </DropdownContent>
              </DropdownMenu>
            ) : null}
            {can("document.create") ? (
              <Button onClick={() => setCreating(true)}>
                <Plus /> Neue Datei
              </Button>
            ) : null}
          </>
        }
      />
      {documents.isLoading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {skeletonKeys(4).map((key) => (
            <Skeleton key={key} className="h-60" />
          ))}
        </div>
      ) : documents.data?.length === 0 ? (
        <EmptyState
          icon={<Plus />}
          title="Noch keine Dateien"
          description="Erstelle einen Social-Media-Post, einen Flyer oder ein Reel – oder lass die KI über MCP einen Entwurf anlegen."
          action={
            can("document.create") ? (
              <Button onClick={() => setCreating(true)}>Neue Datei</Button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {documents.data?.map((document) => (
            <DocumentCard
              key={document.id}
              orgId={orgId}
              document={document}
              menuItems={
                can("document.update") ? (
                  <>
                    <DropdownItem onSelect={() => setRenaming(document)}>
                      <Pencil /> Umbenennen …
                    </DropdownItem>
                    <DropdownItem
                      onSelect={() => duplicate.mutate({ orgId, documentId: document.id })}
                    >
                      <Copy /> Duplizieren
                    </DropdownItem>
                    <DropdownItem onSelect={() => setMoving(document.id)}>
                      <FolderInput /> In anderes Projekt …
                    </DropdownItem>
                    {document.kind === "design" && can("document.create") ? (
                      <DropdownItem onSelect={() => setMultiFormat(document)}>
                        <Scaling /> In mehrere Formate …
                      </DropdownItem>
                    ) : null}
                    <DropdownItem
                      onSelect={() =>
                        markTemplate.mutate({
                          orgId,
                          documentId: document.id,
                          isTemplate: !document.isTemplate,
                        })
                      }
                    >
                      <LayoutTemplate />
                      {document.isTemplate ? "Keine Vorlage mehr" : "Als Vorlage markieren"}
                    </DropdownItem>
                    <DropdownSeparator />
                    <DropdownItem
                      danger
                      onSelect={() => removeDocument.mutate({ orgId, documentId: document.id })}
                    >
                      <Trash2 /> In den Papierkorb
                    </DropdownItem>
                  </>
                ) : null
              }
            />
          ))}
        </div>
      )}
      {multiFormat ? (
        <MultiFormatDialog
          orgId={orgId}
          documentId={multiFormat.id}
          title={multiFormat.title}
          currentPreset={multiFormat.formatPreset}
          open
          onOpenChange={(open) => !open && setMultiFormat(null)}
        />
      ) : null}
      <NewDocumentDialog
        orgId={orgId}
        projectId={projectId}
        open={creating}
        onOpenChange={setCreating}
      />
      {editing && project.data ? (
        <ProjectDialog
          orgId={orgId}
          project={project.data}
          open={editing}
          onOpenChange={setEditing}
        />
      ) : null}
      <NameDialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
        title="Datei umbenennen"
        initial={renaming?.title ?? ""}
        pending={rename.isPending}
        onConfirm={async (title) => {
          if (!renaming) return;
          await rename.mutateAsync({ orgId, documentId: renaming.id, title });
          await invalidate();
          setRenaming(null);
        }}
      />
      {moving ? (
        <MoveDocumentDialog
          orgId={orgId}
          documentId={moving}
          currentProjectId={projectId}
          onClose={() => setMoving(null)}
        />
      ) : null}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        danger
        title="Projekt in den Papierkorb?"
        description="Das Projekt und alle Dateien darin landen im Papierkorb und werden nach 30 Tagen endgültig gelöscht. Medien bleiben in der Mediathek."
        confirmLabel="In den Papierkorb"
        onConfirm={async () => {
          await remove.mutateAsync({ orgId, projectId });
          await invalidate();
          toast.success("Projekt in den Papierkorb verschoben");
          await navigate({ to: "/o/$orgId", params: { orgId } });
        }}
      />
    </>
  );
}
